// =============================================================================
// VandieZ admin-web -- Fraud signals (/fraud)
// =============================================================================
// Lists users showing abuse-pattern signals over a sliding window
// (default 7 days). Sorted by total severity score descending. Click
// a row to open the user detail page for context.
//
// Signals are computed by GET /admin/fraud/signals — a single endpoint
// runs five SQL queries against operational tables (no ML, no
// dependencies on a separate fraud DB). When trips/bids cross 1M rows
// the queries move to a materialised view (Spec doc inside the
// endpoint module).
// =============================================================================

import {
  AlertCircle,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  Shield,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { fraudAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { FraudUserRow } from "@/types/fraud";

const WINDOWS = [
  { label: "Last 24h", days: 1 },
  { label: "Last 7d", days: 7 },
  { label: "Last 30d", days: 30 },
];

function severityClass(score: number): string {
  if (score >= 6) {return "bg-danger-subtle text-danger-subtle-foreground";}
  if (score >= 3) {return "bg-warning-subtle text-warning-subtle-foreground";}
  return "bg-muted text-muted-foreground";
}

const SIGNAL_LABEL: Record<string, string> = {
  many_fcm_tokens: "Many FCM tokens",
  login_failures_spike: "Login failures",
  driver_high_cancel_rate: "Bailing post-accept",
  shipper_high_cancel_rate: "Cancel-rate abuse",
  bid_spam: "Bid spam",
  bid_below_lane_floor: "Below lane floor",
};

export function FraudPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const days = Number.parseInt(searchParams.get("days") ?? "7", 10) || 7;

  const [rows, setRows] = useState<FraudUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRows = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await fraudAdminApi.signals(
          accessToken,
          { days, limit: 100 },
          signal,
        );
        setRows(res.rows);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        setError(
          e instanceof ApiError
            ? e.problem.detail
            : "Couldn't load fraud signals.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken, days],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchRows(c.signal);
    return () => { c.abort(); };
  }, [fetchRows]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Fraud signals"
        description="Users showing abuse-pattern signals. Click any row to open the user detail + drill into their activity timeline."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void fetchRows()}
            disabled={loading}
          >
            <RefreshCw
              className={cn(
                "mr-1 h-4 w-4",
                loading && "animate-spin",
              )}
            />
            Refresh
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" /> Flagged users
          </CardTitle>
          <CardDescription>
            6 signals — many FCM tokens, login-failure spikes, driver
            bailing post-accept, shipper cancellation abuse, bid spam,
            bids below lane floor. Severity 1-3 per signal; sorted by
            total descending.
            {rows.length > 0 && (
              <span className="ml-1 text-foreground">
                {rows.length} flagged.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex gap-1">
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                type="button"
                onClick={() => {
                  setSearchParams(
                    (cur) => {
                      const p = new URLSearchParams(cur);
                      if (w.days === 7) {p.delete("days");} else {p.set("days", String(w.days));}
                      return p;
                    },
                    { replace: true },
                  );
                }}
                className={cn(
                  "rounded px-3 py-1 text-sm",
                  days === w.days
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                {w.label}
              </button>
            ))}
          </div>

          {error !== null && (
            <Alert variant="destructive" className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && rows.length === 0 ? (
            <TableSkeleton rows={5} columns={4} />
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              No suspicious activity in this window. Either the signals
              didn't trigger or there's not enough data yet.
            </div>
          ) : (
            <ul className="divide-y">
              {rows.map((r) => (
                <li key={r.user_id}>
                  <Link
                    to={`/users/${r.user_id}`}
                    className="flex items-start gap-3 px-2 py-3 hover:bg-muted/40 transition-colors"
                  >
                    <div className="mt-0.5 shrink-0">
                      <AlertTriangle className="h-4 w-4 text-warning-subtle-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">
                            {r.full_name ?? r.user_id.slice(0, 8)}
                          </span>
                          <span className="text-xs text-muted-foreground capitalize">
                            ({r.primary_role})
                          </span>
                          <span className="font-mono text-xs text-muted-foreground">
                            {r.phone_masked}
                          </span>
                        </div>
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-xs font-medium",
                            severityClass(r.total_score),
                          )}
                        >
                          score {r.total_score}
                        </span>
                      </div>
                      <ul className="mt-1 space-y-0.5">
                        {r.signals.map((s) => (
                          <li
                            key={s.code}
                            className="text-xs text-muted-foreground flex items-center gap-2"
                          >
                            <span className="font-mono rounded bg-muted px-1.5 py-0.5">
                              {SIGNAL_LABEL[s.code] ?? s.code} (sev {s.severity})
                            </span>
                            <span>{s.detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
